import React, { ReactElement, useState } from 'react';
import { styles } from './ProductCompareTable.css';
import CloseIcon from '@jbhi-fi/lanyard-ui/components/Icons/Close';
import { ProductCompareTableSection } from './ProductCompareTableSection';
import { AskForADeal } from '../../ProductDetail/AskForADeal';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import { HitPriceTag } from '../HitPriceTag/HitPriceTag';
import { CompareResults } from '@jbhi-fi/apis/src/compare';
import Switch from '@jbhi-fi/lanyard-ui/components/Switch';
import { CompareFillerIcon } from './misc/CompareFillerIcon';
import { FullscreenCameraIcon } from '@jbhi-fi/lanyard-ui/components/Icons/FullscreenCamera';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { submitTracking } from '../../Tracking/utils/shopifyActionTrackingHelper';

const SEEN_IT_CHEAPER_MESSAGE = 'Seen it cheaper? Ask for a JB Deal.';

interface ProductCompareTableProps {
  items: CompareResults;
  productCards: Array<ReactElement>;
  actionButtons: Array<ReactElement>;
  shouldShowAskForADeal?: boolean;
  onRemoveItem: (sku: string) => void;
  onScanQRClick: () => void;
}

type CompareResultSection = keyof CompareResults['specifications'];

export const ProductCompareTable = ({
  items,
  productCards,
  actionButtons,
  shouldShowAskForADeal = true,
  onRemoveItem,
  onScanQRClick,
}: ProductCompareTableProps) => {
  const isDesktop = useBreakpoint('md', 'up');
  const [showDifferences, setShowDifferences] = useState(false);
  const isSingleItem = items.hits.length === 1;
  const shouldShowFiller = items.hits.length < (isDesktop ? 2 : 4);

  const handleDifferencesToggle = (checked: boolean) => {
    submitTracking({
      event: 'productCompare',
      action: `differences-button-${checked ? 'on' : 'off'}`,
      trackingDescriptor: { page: 'comparePage' },
      gtm: {},
    });
    setShowDifferences(checked);
  };

  return (
    <table className={styles.table} data-testid="product-compare-table">
      <thead>
        <tr className={styles.tableHeading}>
          <th className={styles.differencesCell}>
            {!isSingleItem ? (
              <Switch
                label={isDesktop ? 'Show differences' : 'Differences'}
                onToggle={handleDifferencesToggle}
                testId="compare-differences"
              />
            ) : null}
          </th>
          {items.hits.map((hit: AlgoliaProductHit, index) => {
            const isPhone = hit.category_hierarchy.includes('Mobile Phones');
            return (
              <th className={styles.tableHeadingCell} key={hit.objectID}>
                <div className={styles.tableHeadingCellContent}>
                  <div>
                    <div className={styles.nonBold}>{hit.product?.brand}</div>
                    <div className={styles.model}>{isPhone ? hit.facets['Phone Type'] : hit.product?.model}</div>
                  </div>
                  <CloseIcon
                    cursor="pointer"
                    onClick={() => onRemoveItem(hit.sku)}
                    fontSize={20}
                    data-testid="compare-remove-product"
                  />
                </div>
              </th>
            );
          })}

          {shouldShowFiller ? <th className={styles.tableHeadingCell}></th> : null}
        </tr>
        <tr>
          <th className={styles.firstColumn}></th>
          {productCards.map((productCard, i) => (
            <th className={styles.productCardCell} key={i}>
              <div className={styles.productCardDiv}>{productCard}</div>
            </th>
          ))}
          {shouldShowFiller ? (
            <td className={styles.productCardCell}>
              <div className={styles.singleItemFiller}>
                {isDesktop ? (
                  <>
                    <CompareFillerIcon className={styles.singleItemFillerIcon} />
                    <b>Add another product to get comparing</b>
                    <Button label="Shop now" type="link" onClick={() => (window.location.href = '/')} />
                  </>
                ) : (
                  <>
                    <FullscreenCameraIcon onClick={onScanQRClick} className={styles.singleItemFillerIcon} />
                    <b>
                      <Button
                        label="Tap here to scan another product"
                        type="link"
                        onClick={onScanQRClick}
                        style={{ textDecoration: 'none' }}
                      />
                    </b>
                  </>
                )}
              </div>
            </td>
          ) : null}
        </tr>
      </thead>
      {(Object.keys(items.specifications) as Array<CompareResultSection>).map(key =>
        Object.keys(items.specifications[key]).length ? (
          <ProductCompareTableSection
            key={key}
            heading={key}
            specs={items.specifications[key]}
            numItems={items.hits.length}
            showDifferences={showDifferences}
            shouldShowFiller={shouldShowFiller}
          />
        ) : null
      )}
      <tfoot>
        <tr>
          <td className={styles.seenItCheaperCell}>
            {isDesktop && shouldShowAskForADeal ? (
              <AskForADeal isVertical isEnabled message={SEEN_IT_CHEAPER_MESSAGE} />
            ) : null}
          </td>
          {actionButtons.map((actionButton: ReactElement, i: number) => {
            return (
              <td key={i} className={styles.ctaCell}>
                <div className={styles.priceTagWrapper}>
                  <HitPriceTag className={styles.priceTag} hit={items.hits[i]} />
                </div>
                {actionButton}
              </td>
            );
          })}
        </tr>
        {!isDesktop && shouldShowAskForADeal ? (
          <tr>
            <td className={styles.seenItCheaperCell}>
              <div className={styles.seenItCheaperMobileDiv}>
                <AskForADeal isEnabled message={SEEN_IT_CHEAPER_MESSAGE} />
              </div>
            </td>
          </tr>
        ) : null}
      </tfoot>
    </table>
  );
};
